import type { AxiosInstance } from "axios";

import { toast } from "sonner";

import type { Product } from "@/types/products";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { usePrivateAxios } from "./use-private-axios";

interface ProductsQueryResponse {
	data: {
		items: Product[];
		meta: {
			totalItems: number;
			itemCount: number;
			itemsPerPage: number;
			totalPages: number;
			currentPage: number;
		};
	};
	message: string;
	isSuccessful: boolean;
}
interface ProductQueryResponse {
	data: Product;
	message: string;
	isSuccessful: boolean;
}

async function fetchProducts(axios: AxiosInstance) {
	const response = await axios.get<ProductsQueryResponse>("/product/list_products");

	return response.data.data;
}
async function fetchProduct(axios: AxiosInstance, productId: string) {
	await new Promise(resolve => setTimeout(resolve, 2000));
	const data = await axios.get<ProductQueryResponse>(`/product/view_product/${productId}`);
	return data.data.data;
}
export function useProducts() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["products"],
		queryFn: () => fetchProducts(privateAxios)
	});
}
export function useProduct(productId: string) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["product", productId],
		enabled: !!productId,
		queryFn: () => fetchProduct(privateAxios, productId)
	});
}
export function useDeleteProduct() {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["delete-product"],
		mutationFn: async (productId: string) => {
			return await privateAxios.delete(`/product/${productId}`);
		},
		onError: () => toast.error("Failed to delete product"),
		onSuccess: () => toast.success("Product deleted successfully"),
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["products"] });
		}
	});
}
